// components/component/checkout/checkout.tsx
'use client';

import { Button } from '@/components/ui/button';
import { useCart } from '@/contexts/CartContext';
import { loadStripe } from '@stripe/stripe-js';
import Image from 'next/image';
import { Card } from '@/components/ui/card';
import { motion } from 'framer-motion';
import {
  Phone as PhoneIcon,
  Mail as MailIcon,
  X as XIcon,
  ShoppingBag,
  Plus,
  ArrowLeft,
  Loader2,
} from 'lucide-react';
import Link from 'next/link';
import { useState } from 'react';

// Import the shared CartItem interface
interface CartItem {
  _id: string;
  name: string;
  price: number;
  image: string;
  brand?: string;
  stripePriceId?: string;
  quantity?: number;
  sku?: string;
}

const stripePromise = loadStripe(
  process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY!
);

export function Checkout() {
  const { cartItems, removeFromCart } = useCart();
  const [isLoading, setIsLoading] = useState(false);

  const totalPrice = cartItems.reduce((total: number, item: CartItem) => {
    return total + (typeof item.price === 'number' ? item.price : 0);
  }, 0);

  const handleStripeCheckout = async () => {
    setIsLoading(true);
    try {
      const stripe = await stripePromise;
      if (!stripe) {
        throw new Error('Stripe has not been properly initialized');
      }

      // Dynamically import the server action
      const { createCheckoutSession } = await import(
        '@/app/actions/checkoutActions'
      );

      // Call the server action with cart items
      const result = await createCheckoutSession(
        cartItems.map((item: CartItem) => ({
          price: item.stripePriceId || '',
          quantity: item.quantity || 1,
        }))
      );

      if (!result.success || !result.sessionId) {
        throw new Error(result.error || 'Failed to create checkout session');
      }

      // Redirect to Stripe checkout
      const stripeResult = await stripe.redirectToCheckout({
        sessionId: result.sessionId,
      });

      if (stripeResult.error) {
        throw new Error(stripeResult.error.message);
      }
    } catch (error) {
      console.error('Checkout error:', error);
      alert(
        error instanceof Error
          ? error.message
          : 'Failed to start checkout process'
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-slate-900 py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center justify-center space-y-6 text-center">
            <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-slate-400">
              Your bag is empty
            </h1>
            <p className="text-slate-400">
              Looks like you haven&apos;t added any watches to your bag yet.
            </p>
            <Link href="/marketplace">
              <Button className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 text-white shadow-lg rounded-xl transition-all duration-300 hover:shadow-blue-500/25 hover:scale-[1.02]">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Continue Shopping
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 py-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Cart Items Section */}
          <div className="flex-1 space-y-6">
            <div className="flex items-center justify-between">
              <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-slate-400">
                Your bag
              </h1>
              <Link href="/marketplace">
                <Button
                  variant="ghost"
                  className="text-slate-400 hover:text-blue-400 transition-colors duration-300"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Continue Shopping
                </Button>
              </Link>
            </div>
            <p className="text-slate-400">{cartItems.length} item(s)</p>

            {/* Cart Items */}
            <motion.div
              className="space-y-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              {cartItems.map((item, index) => (
                <motion.div
                  key={item._id}
                  layout
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <Card className="bg-slate-800/50 backdrop-blur-sm border-slate-700/50 shadow-xl rounded-xl transition-all duration-300 hover:shadow-2xl hover:shadow-blue-500/10 group">
                    <div className="p-4">
                      <div className="flex gap-6">
                        <div className="relative w-24 h-24 rounded-lg overflow-hidden ring-2 ring-slate-700/50 group-hover:ring-blue-500/50 transition-all duration-300">
                          <Image
                            alt={item.name}
                            src={item.image}
                            width={96}
                            height={96}
                            className="object-cover transition-transform duration-300 group-hover:scale-105"
                            priority={index === 0}
                          />
                        </div>

                        <div className="flex-1">
                          <h2 className="text-xl font-semibold text-slate-200 group-hover:text-blue-400 transition-colors duration-300">
                            {item.brand}
                          </h2>
                          <p className="text-slate-400">{item.name}</p>
                          <p className="text-slate-500 text-sm mt-1">
                            ${item.price.toLocaleString()}
                          </p>
                          {item.sku !== undefined && (
                            <p className="text-slate-500 text-sm">
                              SKU: {item.sku}
                            </p>
                          )}
                          <p className="text-slate-500 text-sm">Final Sale</p>
                        </div>

                        <button
                          className="text-slate-400 hover:text-red-400 transition-colors duration-300"
                          onClick={() => removeFromCart(index)}
                          aria-label="Remove item"
                        >
                          <XIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </motion.div>
          </div>

          {/* Order Summary Section */}
          <div className="lg:w-96 space-y-6">
            <Card className="bg-slate-800/50 backdrop-blur-sm border-slate-700/50 shadow-xl rounded-xl p-6 transition-all duration-300 hover:shadow-2xl hover:shadow-blue-500/10 sticky top-6">
              <h2 className="text-xl font-semibold text-slate-200 mb-6">
                Order Summary
              </h2>

              <div className="space-y-4">
                <div className="flex justify-between text-slate-400">
                  <span>Subtotal</span>
                  <span>${totalPrice.toLocaleString()}</span>
                </div>

                <div className="flex justify-between text-slate-400">
                  <span>Shipping</span>
                  <span className="text-blue-400">Free</span>
                </div>

                <div className="flex justify-between text-slate-400">
                  <span>Taxes</span>
                  <span>None</span>
                </div>

                <div className="border-t border-slate-700 pt-4">
                  <div className="flex justify-between text-slate-200 font-semibold">
                    <span>Total</span>
                    <span className="text-xl bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-blue-600">
                      ${totalPrice.toLocaleString()}
                    </span>
                  </div>
                </div>

                <Button
                  onClick={handleStripeCheckout}
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 text-white shadow-lg rounded-xl transition-all duration-300 hover:shadow-blue-500/25 hover:scale-[1.02]"
                  size="lg"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <ShoppingBag className="mr-2 h-4 w-4" />
                      Proceed to Checkout
                    </>
                  )}
                </Button>
              </div>
            </Card>

            {/* Contact Information */}
            <Card className="bg-slate-800/50 backdrop-blur-sm border-slate-700/50 shadow-xl rounded-xl p-6 transition-all duration-300 hover:shadow-2xl hover:shadow-blue-500/10">
              <h3 className="text-lg font-semibold text-slate-200 mb-4">
                Need Help?
              </h3>
              <div className="space-y-4 text-slate-400">
                <p>For questions about your order:</p>

                <div className="flex items-center space-x-2 group">
                  <PhoneIcon className="h-4 w-4 group-hover:text-blue-400 transition-colors duration-300" />
                  <span className="group-hover:text-blue-400 transition-colors duration-300">
                    +1 786 765 7909 (US)
                  </span>
                </div>

                <div className="flex items-center space-x-2 group">
                  <MailIcon className="h-4 w-4 group-hover:text-blue-400 transition-colors duration-300" />
                  <a
                    href="mailto:support@watchinspect.com"
                    className="group-hover:text-blue-400 transition-colors duration-300"
                  >
                    support@watchinspect.com
                  </a>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
